import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  CreateLocationBody,
  api,
  Location,
  UpdateLocationBody,
  UpdateLegacyWarningSettingBody,
  type UpdateMaritimeWarningSettingsBody,
  type UpdatePublicWarningSettingsBody,
} from "@/lib/api";
import { queryKeys } from "@/features/admin/features/dashboards";
import { useInvalidateSelectedDashboard } from "@/features/admin/hooks/use-invalidate-selected-dashboard";

import { reorderLocationsByIds } from "./reorder-locations-by-ids";

export const useCreateDashboardLocationMutation = () => {
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dashboardId,
      body,
    }: {
      dashboardId: string;
      body: CreateLocationBody;
    }) => api.locations.createLocation(dashboardId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardLocations(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useUpdateDashboardLocationMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: string;
      dashboardId: string;
      body: UpdateLocationBody;
    }) => api.locations.updateLocation(locationId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardLocations(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useDeleteDashboardLocationMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({ locationId }: { locationId: string; dashboardId: string }) =>
      api.locations.deleteLocation(locationId),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardLocations(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useReorderDashboardLocationsMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({ dashboardId, ids }: { dashboardId: string; ids: string[] }) =>
      api.locations.reorderLocationsForDashboard(dashboardId, ids),
    onMutate: async ({ dashboardId, ids }) => {
      const queryKey = queryKeys.dashboardLocations(dashboardId);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<Location[]>(queryKey);

      if (previous) {
        queryClient.setQueryData<Location[]>(
          queryKey,
          reorderLocationsByIds(previous, ids),
        );
      }

      return { previous };
    },
    onError: (_error, { dashboardId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.dashboardLocations(dashboardId),
          context.previous,
        );
      }
    },
    onSettled: (_data, _error, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardLocations(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useCreateDashboardWarningMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      dashboardId,
      body,
    }: {
      dashboardId: string;
      body: UpdateLegacyWarningSettingBody;
    }) => api.legacyWarningSettings.updateLegacyWarningSettings(dashboardId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardWarnings(dashboardId) });
      invalidateCurrentDashboard();
    },
  });
};

export const useUpdateDashboardWarningMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      dashboardId,
      body,
    }: {
      dashboardId: string;
      body: UpdateLegacyWarningSettingBody;
    }) => api.legacyWarningSettings.updateLegacyWarningSettings(dashboardId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardWarnings(dashboardId) });
      invalidateCurrentDashboard();
    },
  });
};

/** Public-page settings are independent of the maritime warning domain flags. */
export const useUpdateDashboardPublicWarningSettingsMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      dashboardId,
      body,
    }: {
      dashboardId: string;
      body: UpdatePublicWarningSettingsBody;
    }) => api.publicWarningSettings.updatePublicWarningSettings(dashboardId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardPublicWarningSettings(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};

export const useUpdateDashboardMaritimeWarningSettingsMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      dashboardId,
      body,
    }: {
      dashboardId: string;
      body: UpdateMaritimeWarningSettingsBody;
    }) => api.maritimeWarningSettings.updateMaritimeWarningSettings(dashboardId, body),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardMaritimeWarningSettings(dashboardId),
      });
      invalidateCurrentDashboard();
    },
  });
};
